import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { platformName } from '../platformMeta';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import type { CalendarPost, CalendarSong } from '../types';
import { calendarMessages } from './calendarMessages';
import { postTitle } from './calendarPostView';
import { formatMoveTime, postStateWord } from './postCardLabel';
import type { CalendarPostActions } from './useCalendarPostActions';
import { useDragMove } from './useDragMove';

interface UseCalendarMoveProps {
  view: 'month' | 'week';
  actions: CalendarPostActions;
  songs: Map<string, CalendarSong>;
}

function isBusyError(error: unknown): boolean {
  return error instanceof FYMAPIError && error.statusCode === 409;
}

/**
 * The move of a card (design b6): the drag and the keyboard (`useDragMove`), the call, and the
 * note with Undo. A 409 (the worker took the post) says "This post is going out now, so it cannot
 * move."; the card stays where it was.
 */
export function useCalendarMove({ view, actions, songs }: UseCalendarMoveProps) {
  const intl = useIntl();
  const { showError } = useShowToast();
  const [lastMove, setLastMove] = useState<{ post: CalendarPost; text: string } | null>(null);

  const songOf = useCallback(
    (post: CalendarPost) => (post.catalogItemUuid === null ? undefined : songs.get(post.catalogItemUuid)),
    [songs]
  );

  const onMove = useCallback(
    (post: CalendarPost, scheduledAt: Date) => {
      actions
        .move(post.uuid, scheduledAt, isBusyError)
        .then((result) => {
          if (result.ok) {
            const time = formatMoveTime(intl, scheduledAt);
            const message = post.status === 'draft' ? calendarMessages.movedDraft : calendarMessages.moved;
            setLastMove({ post, text: intl.formatMessage(message, { time }) });
          } else if (isBusyError(result.error)) {
            showError(intl.formatMessage(calendarMessages.moveBusy));
          }
        })
        .catch(console.error);
    },
    [actions, intl, showError]
  );

  const onRefused = useCallback(() => showError(intl.formatMessage(calendarMessages.dropRefused)), [intl, showError]);

  const announce = useMemo(
    () => ({
      pickedUp: (post: CalendarPost) =>
        intl.formatMessage(view === 'week' ? calendarMessages.pickedUpWeek : calendarMessages.pickedUp, {
          platform: platformName(post.platform),
          state: postStateWord(intl, post),
          title: postTitle(post, songOf(post)),
          time: formatMoveTime(intl, new Date(post.scheduledAt)),
        }),
      moveTo: (time: Date) => intl.formatMessage(calendarMessages.moveTo, { time: formatMoveTime(intl, time) }),
      canceled: () => intl.formatMessage(calendarMessages.moveCanceled),
    }),
    [intl, songOf, view]
  );

  const drag = useDragMove({ view, onMove, onRefused, announce });

  /** Moves the last moved post back to its old time. */
  const undo = useCallback(() => {
    if (lastMove === null) {
      return;
    }
    const { post } = lastMove;
    setLastMove(null);
    actions.move(post.uuid, new Date(post.scheduledAt)).catch(console.error);
  }, [actions, lastMove]);

  const closeNote = useCallback(() => setLastMove(null), []);

  return { drag, noteText: lastMove?.text ?? null, undo, closeNote };
}
